import { Button } from "@/common/components/button";
import { RecallWrapper, ViewRecallWrapper } from "./styles";
import { useNavigate } from "react-router-dom";
import Table from "@/common/components/Table";
import {
  distributorAllRecallHeaders,
  distributorAllRecallKeys,
  distributorIncomingTableHeaders,
  distributorIncomingTableKeys,
  distributorOutgoingTableHeaders,
  distributorOutgoingTableKeys,
  getCartonData,
  getDistributorIncomingTableData,
  getDistributorOutgoingTableData,
  getProductData,
  getRequestTableData,
  getResponseTableData,
  handleProduct,
  recallFilterOptions,
  requestTableHeaders,
  requestTableKeys,
  responseTableHeaders,
  responseTableKeys,
  RetailerRequestTableHeaders,
} from "./data";
import useQuery from "@/hooks/useQuery";
import { PRODUCT_RECALL, PRODUCT_URL } from "@/common/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { KeyValueObj } from "@/common/types";
import { Pagination } from "@/common/components/pagination";
import Tab2 from "@/common/components/Tab/Tab2";
import { Modal } from "@/common/components/modal";
import Form from "@/common/components/form/Form";
import RecallProductDetails from "./sub/product-details";
import CartonDetails from "./sub/carton-details";
import DistributorDetails from "./sub/distributor-details";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import RegulatoryNote from "./sub/regulatory-note";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { PageSkeletonLoader } from "@/common/components/skeleton/page-skeleton";
import { debounce } from "@/common/utils/general";
import { useAuthDetails } from "@/hooks/useAuthDetails";

const BASE_RECALL_FORM = [
  {
    inputType: "input",
    loadingState: true,
    prompt: "Product name",
    name: "product_name",
    placeholder: "Loading products",
    fullWidth: false,
    readOnly: true,
  },
  {
    inputType: "input",
    prompt: "Batch type",
    name: "batch_id",
    placeholder: "Select product to load batch",
    fullWidth: false,
    readOnly: true,
  },
];

export enum IAction {
  DISPATCH = "dispatch",
  RECIEVE = "recieve",
  RECALL = "recall",
}

const Recall = () => {
  const navigate = useNavigate();
  const makeRequest = useApiCallHandling();
  const notify = useNotification();
  const { companyType } = useAuthDetails();

  const role = companyType?.toLowerCase();
  const isManufacturer = role === "manufacturer";
  const isDistributor = role === "distributor";
  // const isRetailer = role === "retailer";

  // ── Pagination & filter ──────────────────────────────────────────────
  const [recallCurrentPage, setRecallCurrentPage] = useState(0);
  const [recallPerPage, setRecallPerPage] = useState(25);
  const [status, setStatus] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);

  // ── UI state ─────────────────────────────────────────────────────────
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentRow, setCurrentRow] = useState<KeyValueObj>({});
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [loadingViewMore, setLoadingViewMore] = useState(false);
  const [currentRecallData, setCurrentRecallData] = useState<KeyValueObj>({});
  const [currentRecallSelection, setCurrentRecallSelection] =
    useState<KeyValueObj>({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // ── Manufacturer form state ──────────────────────────────────────────
  const [recallForm, setRecallForm] = useState<any>(BASE_RECALL_FORM);
  const [selectedProduct, setSelectedProduct] = useState<KeyValueObj>({});

  // ── Tab — single state, initial value derived from role ──────────────
  const [currentTab, setCurrentTab] = useState(() =>
    isDistributor ? "All Recall" : "Sent Request",
  );

  // ── Queries ──────────────────────────────────────────────────────────
  const recallQuery = useMemo(() => {
    let q = `${PRODUCT_RECALL}?per_page=${recallPerPage}&page=${recallCurrentPage}`;
    if (status) q += `&status=${status}`;
    if (date) q += `&date=${date}`;
    return q;
  }, [recallPerPage, recallCurrentPage, status, date]);

  const {
    data: recallRequests,
    meta: recallRequestMeta,
    loading: recallRequestLoading,
    refetch: refetchRecallRequests,
  } = useQuery(recallQuery);

  const {
    data: recallResponse,
    meta: recallResponsetMeta,
    loading: recallResponseLoading,
    // refetch: refetchResponseRequests,
  } = useQuery(recallQuery + "?status=recieved", isManufacturer);

  const {
    data: distributorAllRecall,
    meta: distributorRecallMeta,
    loading: distributorRecallLoading,
  } = useQuery(PRODUCT_RECALL + "/affected-products", isDistributor);

  const {
    data: distributorIncoming,
    meta: distributorIncomingMeta,
    loading: distributorIncomingLoading,
  } = useQuery(
    PRODUCT_RECALL + `/affected-products?type=incoming`,
    isDistributor,
  );

  const {
    data: distributorOutgoing,
    meta: distributorOutgoingMeta,
    loading: distributorOutgoingLoading,
  } = useQuery(
    PRODUCT_RECALL + `/affected-products?type=outgoing`,
    isDistributor,
  );

  const {
    data: products,
    loading: productLoading,
    error: productError,
  } = useQuery(PRODUCT_URL + "/dropdown", isManufacturer);

  useEffect(() => {
    if (!isManufacturer) return;
    if (productError) {
      notify({
        type: "ERROR",
        message: "Unable to load products, refresh the page to try again.",
      });
      return;
    }
    if (products && !productLoading) {
      setRecallForm((prev: any) => {
        const copy = [...prev];
        copy[0] = {
          inputType: "selectInput",
          name: "product_name",
          prompt: "Product Name",
          rules: { required: true },
          options: handleProduct(products),
          isFullWidth: false,
          required: true,
        };
        return copy;
      });
    }
  }, [products, productLoading, productError, isManufacturer]);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleFetchBatches = useCallback(
    (productId: string) => {
      setRecallForm((prev: any) => {
        const copy = [...prev];
        copy[1] = {
          inputType: "input",
          name: "batch_number",
          prompt: "Batch Number",
          placeholder: "Batch number loading...",
          fullWidth: false,
          readOnly: true,
          loadingState: true,
        };
        return copy;
      });

      makeRequest(
        PRODUCT_URL + `/${productId}/batches`,
        "GET",
        {},
        (response: KeyValueObj) => {
          setRecallForm((prev: any) => {
            const copy = [...prev];
            copy[1] = {
              inputType: "selectInput",
              name: "batch_number",
              prompt: "Batch Number",
              options: response?.map((item: any) => ({
                title: item?.batch_number,
                id: item?.batch_number,
              })),
              rules: { required: true },
              isFullWidth: false,
              returnId: true,
            };
            return copy;
          });
        },
        console.log,
        console.log,
        { dontNotifyOnSuccess: true },
      );
    },
    [makeRequest],
  );

  const handleFormUpdate = useCallback(
    (name: string, value: any) => {
      if (name !== "product_name") return;
      const userSelection = products?.find((p: any) => p.name === value);
      setSelectedProduct(userSelection ?? {});
      if (userSelection) handleFetchBatches(userSelection.id);
    },
    [products, handleFetchBatches],
  );

  const handleRecall = useCallback(
    (data: any) => {
      setSubmitLoading(true);
      makeRequest(
        PRODUCT_URL +
          `/${selectedProduct?.id}/find_batch?batch_number=${data?.batch_number}`,
        "GET",
        {},
        () => {
          notify({ type: "SUCCESS", message: "Product details fetched" });
          navigate(
            `/dashboard/recall/${selectedProduct?.id}?batch=${data?.batch_number}&action=initiate`,
          );
        },
        console.log,
        () => setSubmitLoading(false),
      );
    },
    [selectedProduct, makeRequest, navigate, notify],
  );

  const handleDropdownClick = useCallback((row: KeyValueObj) => {
    setCurrentRow((prevRow) => {
      const isSameRow = prevRow?.id === row?.id;
      setShowDropdown(!isSameRow ? true : (prev) => !prev);
      // console.log("dropdown clicked row =>", row);
      return isSameRow && showDropdown ? {} : row;
    });
  }, []);

  const handleReceiveClick = useCallback(
    (row: KeyValueObj) => {
      // -- recieve is done by both distributor and manufacturer, so we need to check role to know where to pull data from
      const source =
        role === isManufacturer ? recallRequests : distributorIncoming;
      const responseData = source?.find(
        (req: KeyValueObj) => req?.id === row?.id,
      );
      navigate(
        `/dashboard/recall/${row?.id}?action=${IAction.RECIEVE}&batch=${responseData?.batch_number}`,
      );
    },
    [recallRequests, navigate],
  );

  const handleDispatchClick = useCallback(
    (row: KeyValueObj) => {
      const source = isDistributor ? distributorAllRecall : recallRequests;
      const responseData = source?.find(
        (req: KeyValueObj) => req?.id === row?.id,
      );
      navigate(
        `/dashboard/recall/${row?.id}?action=${IAction.DISPATCH}&batch=${responseData?.batch_number}`,
      );
    },
    [recallRequests, navigate],
  );

  const handleIncomingView = useCallback(() => {
    const recallData = recallRequests?.find(
      (req: KeyValueObj) => req?.id === currentRow?.id,
    );

    if (!recallData) return;
    // console.log("recallData =>", recallData?.product_recall_id);

    setCurrentRecallSelection(recallData);
    setLoadingViewMore(true);
    makeRequest(
      PRODUCT_RECALL + `/${recallData?.id}/recalled-items`,
      "GET",
      {},
      (data) => {
        setCurrentRecallData(data);
      },
      console.log,
      () => setLoadingViewMore(false),
    );
    setOpenPreview(true);
  }, [currentRow?.id, recallRequests, makeRequest]);

  const handleClosePreview = useCallback(() => {
    setShowDropdown(false);
    setCurrentRow({});
    setOpenPreview(false);
  }, []);

  const filterHandler = useCallback((values: KeyValueObj) => {
    setStatus(values?.status ?? null);
    setDate(values?.date ?? null);
  }, []);

  const handleSearch = useCallback(
    (value: string) =>
      refetchRecallRequests(PRODUCT_RECALL + `?&search=${value}`),
    [refetchRecallRequests],
  );

  const debounceSearch = useMemo(
    () => debounce(handleSearch, 500),
    [handleSearch],
  );

  // ── Dropdown options ─────────────────────────────────────────────────
  const hasTrackingId = currentRow?.trackingID?.value !== "--";
  const rowStatus = currentRow?.status?.value;
  const canDispatch = currentRow?.canDispatch?.value;

  const manufacturerSentDropdowns = useMemo(
    () =>
      hasTrackingId ? [{ label: "View", action: handleIncomingView }] : [],
    [hasTrackingId, handleIncomingView],
  );

  const manufacturerResponseDropdowns = useMemo(
    () =>
      hasTrackingId
        ? [
            rowStatus === "active"
              ? {
                  label: "Receive",
                  action: (row: KeyValueObj) => handleReceiveClick(row),
                }
              : { label: "View", action: handleIncomingView },
          ]
        : [],
    [hasTrackingId, rowStatus, handleReceiveClick, handleIncomingView],
  );

  const distributorAllDropdowns = useMemo(
    () =>
      hasTrackingId
        ? [
            canDispatch === "true"
              ? {
                  label: "Dispatch",
                  action: (row: KeyValueObj) => handleDispatchClick(row),
                }
              : //
                { label: "View", action: handleIncomingView },
          ]
        : [],
    [hasTrackingId, canDispatch, handleDispatchClick, handleIncomingView],
  );

  const retailerAllDropdowns = useMemo(
    () =>
      hasTrackingId
        ? [
            canDispatch === "true"
              ? {
                  label: "Dispatch",
                  action: (row: KeyValueObj) => handleDispatchClick(row),
                }
              : { label: "View", action: handleIncomingView },
          ]
        : [],
    [hasTrackingId, canDispatch, handleDispatchClick, handleIncomingView],
  );

  const distIncomingDropdowns = useMemo(
    () =>
      hasTrackingId
        ? [
            rowStatus === "received"
              ? {
                  label: "Receive",
                  action: (row: KeyValueObj) => handleReceiveClick(row),
                }
              : { label: "View", action: handleIncomingView },
          ]
        : [],
    [hasTrackingId, rowStatus, handleReceiveClick, handleIncomingView],
  );

  const distOutgoingDropdowns = useMemo(
    () =>
      hasTrackingId
        ? [
            rowStatus === "pending"
              ? {
                  label: "Recieve",
                  action: (row: KeyValueObj) => handleReceiveClick(row),
                }
              : { label: "View", action: handleIncomingView },
          ]
        : [],
    [hasTrackingId, rowStatus, handleReceiveClick, handleIncomingView],
  );

  // ── Shared pagination props ──────────────────────────────────────────
  const paginationProps = {
    onNextPage: (e: KeyValueObj) => setRecallCurrentPage(e.selected),
    onPerPageChange: (e: KeyValueObj) => setRecallPerPage(e?.target?.value),
  };

  // ── Tab definitions ──────────────────────────────────────────────────
  const manufacturerTabs = useMemo(
    () => [
      {
        title: "Sent Request",
        component: (
          <div className="table-wrapper">
            <Table
              tableHeaders={requestTableHeaders}
              tableKeys={requestTableKeys}
              tableData={getRequestTableData(recallRequests)}
              tableTitle="Recall Request"
              filterInputs={recallFilterOptions}
              handleFilter={filterHandler}
              tableSubtitle={
                "Total request sent: " + recallRequestMeta?.total_count
              }
              loading={recallRequestLoading}
              handleDropDownClicked={handleDropdownClick}
              showDropDown={showDropdown}
              dropDownOptions={manufacturerSentDropdowns}
              handleSearch={debounceSearch}
              spaced
              rowLineColor="light"
            />
            <Pagination
              pageCount={recallRequestMeta?.total_pages}
              {...paginationProps}
            />
          </div>
        ),
      },
      {
        title: "Recall Response",
        component: (
          <div className="table-wrapper">
            <Table
              tableHeaders={responseTableHeaders}
              tableKeys={responseTableKeys}
              tableData={getResponseTableData(recallResponse)}
              tableTitle="Recall Response"
              tableSubtitle={
                "Total responses: " + recallResponsetMeta?.total_count
              }
              loading={recallResponseLoading}
              handleDropDownClicked={handleDropdownClick}
              showDropDown={showDropdown}
              dropDownOptions={manufacturerResponseDropdowns}
              spaced
              rowLineColor="light"
            />
            <Pagination
              pageCount={recallRequestMeta?.total_pages}
              {...paginationProps}
            />
          </div>
        ),
      },
    ],
    [
      recallRequests,
      recallRequestMeta,
      recallRequestLoading,
      showDropdown,
      manufacturerSentDropdowns,
      manufacturerResponseDropdowns,
      handleDropdownClick,
      filterHandler,
      debounceSearch,
    ],
  );

  const distributorTabs = useMemo(
    () => [
      {
        title: "All Recall",
        component: (
          <div className="table-wrapper">
            <Table
              tableHeaders={distributorAllRecallHeaders}
              tableKeys={distributorAllRecallKeys}
              tableData={getRequestTableData(distributorAllRecall)}
              // tableData={dummyDistributorRecallData}
              tableTitle="Recall Request"
              handleDropDownClicked={handleDropdownClick}
              spaced
              rowLineColor="light"
              loading={distributorRecallLoading}
              showDropDown={showDropdown}
              dropDownOptions={distributorAllDropdowns}
            />
            <Pagination
              pageCount={distributorRecallMeta?.total_pages}
              {...paginationProps}
            />
          </div>
        ),
      },
      {
        title: "Incoming",
        component: (
          <div className="table-wrapper">
            <Table
              tableHeaders={distributorIncomingTableHeaders}
              tableKeys={distributorIncomingTableKeys}
              tableData={getDistributorIncomingTableData(distributorIncoming)}
              tableTitle="Incoming Recall"
              handleDropDownClicked={handleDropdownClick}
              showDropDown={showDropdown}
              dropDownOptions={distIncomingDropdowns}
              rowLineColor="light"
              loading={distributorIncomingLoading}
            />
            <Pagination
              pageCount={distributorIncomingMeta?.total_pages}
              {...paginationProps}
            />
          </div>
        ),
      },
      {
        title: "Outgoing",
        component: (
          <div className="table-wrapper">
            <Table
              tableHeaders={distributorOutgoingTableHeaders}
              tableKeys={distributorOutgoingTableKeys}
              tableData={getDistributorOutgoingTableData(distributorOutgoing)}
              tableTitle="Outgoing Recall"
              handleDropDownClicked={handleDropdownClick}
              showDropDown={showDropdown}
              dropDownOptions={distOutgoingDropdowns}
              loading={distributorOutgoingLoading}
              rowLineColor="light"
            />
            <Pagination
              pageCount={distributorOutgoingMeta?.total_pages}
              {...paginationProps}
            />
          </div>
        ),
      },
    ],
    [
      recallRequests,
      recallRequestMeta,
      distributorIncoming,
      distributorIncomingMeta,
      distributorIncomingLoading,
      distributorOutgoing,
      distributorOutgoingMeta,
      distributorOutgoingLoading,
      showDropdown,
      distIncomingDropdowns,
      distOutgoingDropdowns,
      handleDropdownClick,
    ],
  );

  // ── Shared preview modal — all roles use the same markup ─────────────
  const previewModal = (
    <Modal
      title="Product Details"
      isOpen={openPreview}
      close={handleClosePreview}
    >
      <div className="modal w-[800px] md:w-[1000px]">
        {loadingViewMore ? (
          <PageSkeletonLoader />
        ) : (
          <ViewRecallWrapper>
            <div className="containerWrapper">
              <RecallProductDetails data={getProductData(currentRecallData)} />
              <RegulatoryNote
                reason={
                  currentRecallSelection?.reason ||
                  "No reason added to this recall"
                }
                regulatoryNote={
                  currentRecallSelection?.regulatory_note ||
                  "No regulatory note added for this recall"
                }
              />
              <CartonDetails data={getCartonData(currentRecallData)} />
              <DistributorDetails
                showResponse
                data={currentRecallData?.distributors}
              />
            </div>
          </ViewRecallWrapper>
        )}
      </div>
    </Modal>
  );

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <RecallWrapper>
      {isManufacturer && (
        <>
          <div className="top-wrapper">
            <Button onClick={() => setShowRecallModal(true)}>
              Recall Product
            </Button>
          </div>
          <Tab2
            onTabClick={setCurrentTab}
            currentTab={currentTab}
            tabs={manufacturerTabs}
          />
          <Modal
            title="Recall Request Details"
            isOpen={showRecallModal}
            close={() => setShowRecallModal(false)}
          >
            <div className="modal max-w-[600px] md:w-[500px]">
              <Form
                formInputs={recallForm}
                processInputs={handleRecall}
                loadingState={submitLoading}
                externalUpdateData={handleFormUpdate}
              />
            </div>
          </Modal>
        </>
      )}

      {isDistributor && (
        <Tab2
          onTabClick={setCurrentTab}
          currentTab={currentTab}
          tabs={distributorTabs}
        />
      )}

      {role === "retailer" && (
        <div className="table-wrapper">
          <Table
            tableHeaders={RetailerRequestTableHeaders}
            tableKeys={requestTableKeys}
            tableData={getRequestTableData(recallRequests)}
            tableTitle="Recall Request"
            filterInputs={recallFilterOptions}
            handleFilter={filterHandler}
            tableSubtitle={
              "Total request received: " + recallRequestMeta?.total_count
            }
            loading={recallRequestLoading}
            handleDropDownClicked={handleDropdownClick}
            showDropDown={showDropdown}
            dropDownOptions={retailerAllDropdowns}
            handleSearch={debounceSearch}
            spaced
            rowLineColor="light"
          />
          <Pagination
            pageCount={recallRequestMeta?.total_pages}
            {...paginationProps}
          />
        </div>
      )}

      {previewModal}
    </RecallWrapper>
  );
};

export default Recall;
